// The Coach's progress as bars and squares (#149), for a phone on its side:
// the same three things the rings show, laid out like the Khronos timer's
// own face. A bar for the step in progress, then a row of squares for the
// steps in this round and one for the rounds. Done squares are solid, the
// current one fills (and gently dims and brightens while running), and the
// ones to come are dim grey.
//
// Takes the rings' own items (useWorkoutUiState), so both faces always agree.

import React from "react";
import { cn } from "../../lib/utils";

const COLOUR_VAR = {
  work: "--work-color",
  rest: "--rest-color",
  warm: "--warmup-color",
  cool: "--cooldown-color",
  group: "--group-color",
  workout: "--workout-color",
  countin: "--countin-color",
};

const kindOf = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "muted") return "muted";
  if (t.includes("count")) return "countin";
  if (t.includes("warm")) return "warm";
  if (t.includes("cool")) return "cool";
  if (t.includes("rest")) return "rest";
  if (t.includes("group")) return "group";
  if (t.includes("workout")) return "workout";
  return "work";
};

export const colourOf = (type) => {
  const kind = kindOf(type);
  return kind === "muted" ? "rgba(255,255,255,0.4)" : `hsl(var(${COLOUR_VAR[kind]}))`;
};

const DIM = "rgba(255,255,255,0.12)";
const MAX_SQUARES = 16;

const clamp01 = (n) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/** How full the step's bar is, and whether it has no end (reps). */
export const barState = (item) => {
  if (!item) return null;
  if (item.indeterminate) return { fill: 1, open: true };
  if (!(item.total > 0)) return null;
  // Rest and count-in store what's left; the bar drains with them.
  return { fill: clamp01(item.current / item.total), open: false };
};

/** One square per segment, or null when there are too many to draw. */
export const squaresOf = (item) => {
  if (!item) return null;
  if (item.proportionalSegments?.length) {
    return item.proportionalSegments.map((s) => ({
      type: s.type,
      weight: Math.max(1, s.duration || 1),
      fill: s.completed ? 1 : s.active ? clamp01(s.progress) : 0,
      active: !!s.active,
    }));
  }
  const n = item.segmentItems?.length || item.segments;
  if (!n || n > MAX_SQUARES) return null;
  const done = Math.max(0, Math.min(n, item.completed || 0));
  const active = item.active != null ? item.active : done < n ? done : -1;
  return Array.from({ length: n }, (_, i) => ({
    type: item.segmentItems?.[i]?.type || item.type,
    weight: 1,
    fill: i === active ? (item.discrete ? 1 : clamp01(item.currentProgress)) : i < done ? 1 : 0,
    active: i === active,
  }));
};

const Squares = ({ squares, item, running, label }) => {
  if (!squares) {
    const n = item.segmentItems?.length || item.segments;
    return (
      <p className="font-timer text-sm tabular-nums text-white/60" aria-label={label}>
        {`${Math.min(n, (item.active ?? item.completed ?? 0) + 1)} / ${n}`}
      </p>
    );
  }
  return (
    <div className="flex w-full gap-1.5" role="img" aria-label={label}>
      {squares.map((sq, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={cn("relative h-4 overflow-hidden rounded-[3px]", sq.active && running && "animate-pulse-slow")}
          style={{ flexGrow: sq.weight, flexBasis: 0, background: DIM }}
        >
          <span
            className="absolute inset-y-0 left-0"
            style={{ width: `${sq.fill * 100}%`, background: colourOf(sq.type) }}
          />
        </span>
      ))}
    </div>
  );
};

const SegmentFace = ({ items = [], running = true, className, style }) => {
  const [step, ...rest] = items;
  const bar = barState(step);
  const rows = rest.filter(Boolean);
  return (
    <div className={cn("flex w-full flex-col gap-2.5", className)} style={style}>
      {bar && (
        <div className="relative h-3 w-full overflow-hidden rounded-full" style={{ background: DIM }} aria-hidden="true">
          <span
            className={cn(
              "absolute inset-y-0 left-0 rounded-full transition-[width] duration-300 ease-linear",
              bar.open && "opacity-60",
              bar.open && running && "animate-pulse-slow",
            )}
            style={{ width: `${bar.fill * 100}%`, background: colourOf(step.type) }}
          />
        </div>
      )}
      {rows.map((item, i) => {
        const squares = squaresOf(item);
        const n = squares?.length || item.segmentItems?.length || item.segments || 0;
        const label = i === rows.length - 1 && rows.length > 1 ? `Round progress, ${n} rounds` : `Steps in this round, ${n}`;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Squares key={i} squares={squares} item={item} running={running} label={label} />
        );
      })}
    </div>
  );
};

export default SegmentFace;
